import { useEffect, useRef, useState } from 'react';

/** Away this long (tab hidden, or the machine asleep) and the connection is renewed on return. */
export const RECONNECT_AFTER_MS = 5 * 60_000;
const TICK_MS = 60_000;

/** `YYYY-MM-DD` for now, in `timeZone`. */
export function dayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/**
 * Today's date in `timeZone`, kept current for a page that stays open — and a
 * call to `onReturn` when the owner comes back after being away.
 *
 * A screen that computes "today" while rendering keeps showing the day it was
 * opened on: a tab left open overnight greets the owner with yesterday's date,
 * yesterday highlighted on the calendar and yesterday's call counts. This
 * changes value when the date does, so the caller can re-render (or re-key) on
 * it. It is checked once a minute and the moment the tab is shown or focused,
 * because background tabs have their timers throttled — or frozen — and the
 * return is when it matters.
 *
 * "Away" is either the tab being hidden for `RECONNECT_AFTER_MS`, or a gap that
 * long between two ticks of the minute timer: a computer that went to sleep
 * with the tab in front never reports it hidden, but it does miss its ticks.
 */
export function useCurrentDay(timeZone: string, onReturn?: () => void): string {
  const [today, setToday] = useState(() => dayIn(timeZone));
  const onReturnRef = useRef(onReturn);
  onReturnRef.current = onReturn;

  useEffect(() => {
    // A string state: setting the same date again does not re-render.
    const check = () => setToday(dayIn(timeZone));
    check();

    let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
    let lastTick = Date.now();

    const returned = () => {
      check();
      onReturnRef.current?.();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      lastTick = Date.now();
      if (away >= RECONNECT_AFTER_MS) returned();
      else check();
    };

    // Restored from the back/forward cache: the page was frozen whole, for any length of time.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) returned();
    };

    const timer = window.setInterval(() => {
      const now = Date.now();
      const slept = now - lastTick >= RECONNECT_AFTER_MS;
      lastTick = now;
      // A hidden tab's late tick is throttling, not sleep; its return is handled above.
      if (slept && document.visibilityState === 'visible') returned();
      else check();
    }, TICK_MS);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [timeZone]);

  return today;
}
